import React, { useState } from "react";
import {
  TableCell,
  TableCellLayout,
  TableRow,
  TableRowData as RowStateBase,
} from "@fluentui/react-components/unstable";
import { IExpectedHoursItem } from "../../services/types/expectedHours";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import { CheckmarkCircleRegular, MoreHorizontalRegular, WarningRegular } from "@fluentui/react-icons";
import { AppConfirmdialog } from "../common";
import { usersService } from "../../services";
import { ListChildComponentProps } from "react-window";
import styles from "./ExpectedHoursRowStyles";
import { getContractTypeLabel } from "../../utils/contractType";
import PayoutBreakdownInfoButton from "./PayoutBreakdownInfoButton";
import YouTrackBreakdownInfoButton from "./YouTrackBreakdownInfoButton";
import {
  cooperationEndStatusLines,
  earlyServiceEndWarningLabel,
  getComplianceMeta,
  isAcceptBlocked,
  resolveAcceptBlockedReasons,
  resolveAdminDecision,
  resolveDecisionBadgePresentation,
  resolveRowTone,
  resolveYouTrackSettlementMeta,
} from "./expectedHoursRowPresentation";

interface ReactWindowRenderFnProps extends ListChildComponentProps {
  data: TableRowData[];
  acceptSettlement: (item: IExpectedHoursItem) => Promise<void>;
  rejectSettlement: (item: IExpectedHoursItem, rejectionReason: string) => Promise<void>;
  refreshList: () => void;
  canManageActions: boolean;
}

interface TableRowData extends RowStateBase<IExpectedHoursItem> { }

function baselineStaleHintTitle(item: IExpectedHoursItem): string | undefined {
  if (!item.baselineDataChangeReason) return undefined;
  return item.baselineDataChangedAtUtc
    ? `${item.baselineDataChangeReason} (${item.baselineDataChangedAtUtc})`
    : item.baselineDataChangeReason;
}

const complianceIconSize = 20;

function renderWarningIcon(label: string, iconStyle: React.CSSProperties) {
  return (
    <Tooltip content={label} relationship="label">
      <span
        role="img"
        aria-label={label}
        style={styles.complianceIconTrigger}
        tabIndex={0}
      >
        <WarningRegular aria-hidden style={iconStyle} />
      </span>
    </Tooltip>
  );
}

function renderAcceptBlockedWarning(reasons: string[]) {
  if (reasons.length === 0) {
    return null;
  }

  const ariaLabel = reasons.join(". ");
  const tooltipContent =
    reasons.length === 1 ? (
      reasons[0]
    ) : (
      <div>
        Nie można zaakceptować rozliczenia:
        <ul style={styles.acceptBlockedReasonsList}>
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </div>
    );

  return (
    <Tooltip content={tooltipContent} relationship="label">
      <span
        role="img"
        aria-label={ariaLabel}
        style={styles.complianceIconTrigger}
        tabIndex={0}
      >
        <WarningRegular
          aria-hidden
          style={styles.acceptBlockedWarningIcon}
        />
      </span>
    </Tooltip>
  );
}

const ExpectedHoursRow: React.FC<ReactWindowRenderFnProps> = ({
  data,
  index,
  style,
  acceptSettlement,
  rejectSettlement,
  refreshList,
  canManageActions
}) => {
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openClearanceDialog, setOpenClearanceDialog] = useState(false);
  const [openDelayedDialog, setOpenDelayedDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectValidationError, setRejectValidationError] = useState("");

  const row = data[index];
  const item = row.item;
  const adminDecision = resolveAdminDecision(item);
  const rowTone = resolveRowTone(item);
  const complianceMeta = getComplianceMeta(item);
  const badgePresentation = resolveDecisionBadgePresentation(adminDecision);
  const youTrackSettlementMeta = resolveYouTrackSettlementMeta(item);
  const acceptBlocked = isAcceptBlocked(item);
  const acceptBlockedReasons = resolveAcceptBlockedReasons(item);
  const earlyEndWarning = earlyServiceEndWarningLabel(item);
  const cooperationStatus = cooperationEndStatusLines(item);
  const showClearanceAction = item.canSettleClearance;
  const showDelayedAction = item.canSettleDelayedAcceptance;
  const showCooperationEndMenu = showClearanceAction || showDelayedAction;

  const resetRejectDialogState = () => {
    setRejectionReason("");
    setRejectValidationError("");
  };

  const handleOpenRejectDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    resetRejectDialogState();
    setOpenRejectDialog(true);
  };

  const handleOpenAcceptDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenAcceptDialog(true);
  };

  const settleCooperationEnd = async (action: "clearance" | "delayed") => {
    setIsSubmitting(true);
    try {
      if (action === "clearance") {
        await usersService.settleCooperationEndClearance(item.userId, item.year, item.month);
        setOpenClearanceDialog(false);
      } else {
        await usersService.settleCooperationEndDelayedAcceptance(item.userId, item.year, item.month);
        setOpenDelayedDialog(false);
      }
      refreshList();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await acceptSettlement(item);
      setOpenAcceptDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    const trimmedReason = rejectionReason.trim();
    if (!trimmedReason) {
      setRejectValidationError("Powód odrzucenia jest wymagany.");
      return;
    }

    setRejectValidationError("");
    setIsSubmitting(true);
    try {
      await rejectSettlement(item, trimmedReason);
      setOpenRejectDialog(false);
      resetRejectDialogState();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderYouTrackSettlementIcon = () => {
    if (!youTrackSettlementMeta) {
      return null;
    }

    const iconStyle = {
      fontSize: complianceIconSize,
      ...(youTrackSettlementMeta.iconKind === "consistent"
        ? styles.complianceIconConsistent
        : styles.complianceIconInconsistent),
    };

    return (
      <Tooltip content={youTrackSettlementMeta.label} relationship="label">
        <span
          role="img"
          aria-label={youTrackSettlementMeta.label}
          style={styles.complianceIconTrigger}
          tabIndex={0}
        >
          {youTrackSettlementMeta.iconKind === "consistent" ? (
            <CheckmarkCircleRegular aria-hidden style={iconStyle} />
          ) : (
            <WarningRegular aria-hidden style={iconStyle} />
          )}
        </span>
      </Tooltip>
    );
  };

  const renderYouTrackCell = (hours: number) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span>{hours}</span>
      {renderYouTrackSettlementIcon()}
      {hours > 0 && (
        <YouTrackBreakdownInfoButton
          userId={item.userId}
          year={item.year}
          month={item.month}
          ariaLabel={`Szczegóły YouTrack — ${item.userName}`}
        />
      )}
    </span>
  );

  const renderPayoutCell = (hours: number, bucket: 100 | 150 | 200) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span>{hours}</span>
      {hours !== 0 && (
        <PayoutBreakdownInfoButton
          userId={item.userId}
          year={item.year}
          month={item.month}
          bucket={bucket}
          ariaLabel={`Szczegóły nadgodzin ${bucket}% — ${item.userName}`}
        />
      )}
    </span>
  );

  const renderComplianceIcon = () => {
    const iconStyle = {
      fontSize: complianceIconSize,
      ...(complianceMeta.iconKind === "consistent"
        ? styles.complianceIconConsistent
        : styles.complianceIconInconsistent),
    };

    return (
      <Tooltip content={complianceMeta.label} relationship="label">
        <span
          role="img"
          aria-label={complianceMeta.label}
          style={styles.complianceIconTrigger}
          tabIndex={0}
        >
          {complianceMeta.iconKind === "consistent" ? (
            <CheckmarkCircleRegular aria-hidden style={iconStyle} />
          ) : (
            <WarningRegular aria-hidden style={iconStyle} />
          )}
        </span>
      </Tooltip>
    );
  };

  const renderDecisionBadge = () => (
    <Badge
      shape="rounded"
      size="medium"
      appearance={badgePresentation.appearance}
      color={badgePresentation.color}
      style={styles.decisionBadge}
    >
      {badgePresentation.label}
    </Badge>
  );

  const showBaselineStaleHint =
    item.baselineDataChangedAfterDecision && item.settlementStatus;

  const baselineHint = showBaselineStaleHint ? (
    <span style={styles.baselineStaleHint} title={baselineStaleHintTitle(item)}>
      Zmiana danych po decyzji
    </span>
  ) : null;

  const renderStatusColumn = () => (
    <TableCell style={styles.statusCell}>
      <TableCellLayout style={styles.statusCellLayout}>
        <div style={styles.statusStack}>
          <div style={styles.statusTopRow}>
            {renderComplianceIcon()}
            {renderDecisionBadge()}
          </div>
          {baselineHint}
        </div>
      </TableCellLayout>
    </TableCell>
  );

  const renderActionsColumn = () => (
    <TableCell style={styles.expectedHoursActionsCell}>
      <div style={styles.expectedHoursActionsCellLayout}>
        {canManageActions ? (
          <>
            <span style={styles.acceptBlockedWarningSlot}>
              {renderAcceptBlockedWarning(acceptBlockedReasons)}
            </span>
            <span style={styles.acceptBlockedWarningSlot}>
              {earlyEndWarning
                ? renderWarningIcon(earlyEndWarning, styles.earlyServiceEndWarningIcon)
                : null}
            </span>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  size="small"
                  appearance="outline"
                  icon={<MoreHorizontalRegular />}
                  disabled={isSubmitting}
                  aria-label="Akcje"
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {adminDecision !== "accepted" && (
                    <MenuItem
                      onClick={handleOpenAcceptDialog}
                      disabled={acceptBlocked}
                    >
                      Zaakceptuj
                    </MenuItem>
                  )}
                  {adminDecision !== "rejected" && (
                    <MenuItem onClick={handleOpenRejectDialog}>Odrzuć</MenuItem>
                  )}
                  {cooperationStatus && (
                    <>
                      <MenuDivider />
                      <div style={styles.cooperationEndMenuHeader}>Rozliczenie zakończenia współpracy</div>
                      {cooperationStatus.map((line) => (
                        <div key={line} style={styles.cooperationEndMenuStatus}>{line}</div>
                      ))}
                      {showClearanceAction && (
                        <MenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenClearanceDialog(true);
                          }}
                        >
                          Rozlicz obiegówkę
                        </MenuItem>
                      )}
                      {showDelayedAction && (
                        <MenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenDelayedDialog(true);
                          }}
                        >
                          Rozlicz odroczony odbiór
                        </MenuItem>
                      )}
                    </>
                  )}
                </MenuList>
              </MenuPopover>
            </Menu>
          </>
        ) : null}
      </div>
    </TableCell>
  );

  return (
    <>
      <TableRow
        style={{
          ...style,
          ...(rowTone === "attention" ? styles.rowAttention : {}),
        }}
      >
        <TableCell>{item.userName}</TableCell>
        <TableCell>{getContractTypeLabel(item.contractType)}</TableCell>
        <TableCell>{renderYouTrackCell(item.youtrack)}</TableCell>
        <TableCell>{item.absentify}</TableCell>
        <TableCell>{item.toCollect}</TableCell>
        <TableCell>{item.collected}</TableCell>
        <TableCell>{item.realizedToCollectOvertime}</TableCell>
        <TableCell>{renderPayoutCell(item.timePaidout100, 100)}</TableCell>
        <TableCell>{renderPayoutCell(item.timePaidout150, 150)}</TableCell>
        <TableCell>{renderPayoutCell(item.timePaidout200, 200)}</TableCell>
        <TableCell>{item.total}</TableCell>
        <TableCell>{item.workingHours}</TableCell>
        <TableCell>{item.target}</TableCell>
        <TableCell>{item.acceptedTotal != null ? item.acceptedTotal : "—"}</TableCell>
        {renderStatusColumn()}
        {renderActionsColumn()}
      </TableRow>
      <AppConfirmdialog
        title="Rozliczenie obiegówki"
        bodyTxt={`Czy na pewno rozliczyć obiegówkę dla ${item.userName}?`}
        setOpen={setOpenClearanceDialog}
        open={openClearanceDialog}
        onAccept={() => settleCooperationEnd("clearance")}
        isLoading={isSubmitting}
      />
      <AppConfirmdialog
        title="Rozliczenie odroczonego odbioru"
        bodyTxt={`Czy na pewno rozliczyć odroczony odbiór usług dla ${item.userName}? Dotyczy projektów, w których protokół i płatność są przesunięte względem miesiąca świadczenia usług.`}
        setOpen={setOpenDelayedDialog}
        open={openDelayedDialog}
        onAccept={() => settleCooperationEnd("delayed")}
        isLoading={isSubmitting}
      />
      <AppConfirmdialog
        title="Akceptacja rozliczenia"
        bodyTxt={`Czy na pewno chcesz zaakceptować rozliczenie dla ${item.userName}?`}
        setOpen={setOpenAcceptDialog}
        open={openAcceptDialog}
        onAccept={handleAccept}
        isLoading={isSubmitting}
      />
      <Dialog open={openRejectDialog} onOpenChange={(_, dialogData) => setOpenRejectDialog(dialogData.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Odrzucenie rozliczenia</DialogTitle>
            <DialogContent>
              Podaj powód odrzucenia dla {item.userName}.
              <div style={styles.dialogContentWrapper}>
                <Textarea
                  style={styles.textarea}
                  value={rejectionReason}
                  onChange={(_, textareaData) => {
                    setRejectionReason(textareaData.value);
                    if (textareaData.value.trim()) {
                      setRejectValidationError("");
                    }
                  }}
                />
                {rejectValidationError && (
                  <div style={styles.validationError}>{rejectValidationError}</div>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Anuluj</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleReject} disabled={isSubmitting}>
                Odrzuć
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default ExpectedHoursRow;
